import { usePermission } from '../../../hooks/usePermission';
import './unlock-center.css';
import Combobox from '../../../components-ui/spark/Combobox';
import BootstrapTable from '../../../components-ui/bootstrap-table/BootstrapTable';
import Dialog from '../../../components-ui/spark/Dialog';
import DialogActions from '../../../components-ui/spark/DialogActions';
import DialogContent from '../../../components-ui/spark/DialogContent';
import DialogTitle from '../../../components-ui/spark/DialogTitle';
import Button from '../../../components-ui/button/Button';
import IconButton from '../../../components-ui/spark/IconButton';
import TextField from '../../../components-ui/spark/TextField';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Moment from 'react-moment';
import { Badge, Box, Chip, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneIcon from '@mui/icons-material/Done';
import { DiemService, UnlockRequestItem } from '../../../services/diem/diemService';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useCombobox } from '../../../hooks/useCombobox';
import { NotifyHelper } from '../../../helpers/toast';

type BellTabKey = 'manage' | 'mine';
type ReadStateMap = Record<string, boolean>;

const getReadStorageKey = (userKey: string) => `unlock-request-read::${userKey || 'anonymous'}`;

const statusChip = (status: UnlockRequestItem['status']) => {
    if (status === 'approved') {
        return <Chip size="small" label="Đã duyệt" color="success" sx={{ fontWeight: 700 }} />;
    }
    if (status === 'rejected') {
        return <Chip size="small" label="Từ chối" color="error" sx={{ fontWeight: 700 }} />;
    }
    return <Chip size="small" label="Chờ duyệt" color="warning" sx={{ fontWeight: 700 }} />;
};

const mergeUnlockItem = (existing: UnlockRequestItem | undefined, incoming: UnlockRequestItem) => {
    if (!existing) return incoming;
    return {
        ...existing,
        ...incoming,
        ky_hieu_lop: incoming.ky_hieu_lop ?? existing.ky_hieu_lop,
        ten_lop: incoming.ten_lop ?? existing.ten_lop,
        ten_mon: incoming.ten_mon ?? existing.ten_mon,
        so_thi_sinh: incoming.so_thi_sinh ?? existing.so_thi_sinh,
        requested_by: incoming.requested_by ?? existing.requested_by,
        requested_at: incoming.requested_at ?? existing.requested_at,
        requested_reason: incoming.requested_reason ?? existing.requested_reason,
        student_details: incoming.student_details ?? existing.student_details,
        approved_by: incoming.approved_by ?? existing.approved_by,
        approved_at: incoming.approved_at ?? existing.approved_at,
        rejected_by: incoming.rejected_by ?? existing.rejected_by,
        rejected_at: incoming.rejected_at ?? existing.rejected_at,
        rejected_reason: incoming.rejected_reason ?? existing.rejected_reason
    };
};

const formatLopText = (item: UnlockRequestItem, lopLabel?: string | null) => {
    const parts = [item.ky_hieu_lop, item.ten_lop || lopLabel].filter(Boolean);
    return parts.length > 0 ? parts.join(' - ') : '-';
};

const UnlockRequestBell: React.FC = () => {
    useCombobox();

    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<BellTabKey>('mine');
    const [pending, setPending] = useState<UnlockRequestItem[]>([]);
    const [mine, setMine] = useState<UnlockRequestItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [processing, setProcessing] = useState<number|null>(null);
    const [keyword, setKeyword] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [rejecting, setRejecting] = useState<UnlockRequestItem | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [readState, setReadState] = useState<ReadStateMap>({});
    const [detailItem, setDetailItem] = useState<UnlockRequestItem | null>(null);

    const giaoViens = useAppSelector((state) => state.giaoVien.listData);
    const danhSachLop = useAppSelector((state) => state.danhSachLop.listData);
    const authUser = useAppSelector((state) => state.auth.user as any);

    const userKey = useMemo(() => {
        return String(authUser?.id ?? authUser?.sub ?? authUser?.username ?? authUser?.userName ?? authUser?.ID_cb ?? 'anonymous');
    }, [authUser]);

    const {hasAction}=usePermission();
    const canManageRequests=hasAction({module:'BSKT_DIEM',action:'APPROVE_UNLOCK',code:'BSKT_DIEM_DUYET'});

    useEffect(() => {
        try {
            const raw = localStorage.getItem(getReadStorageKey(userKey));
            setReadState(raw ? JSON.parse(raw) : {});
        } catch {
            setReadState({});
        }
    }, [userKey]);

    const persistReadState = useCallback(
        (nextState: ReadStateMap) => {
            setReadState(nextState);
            try {
                localStorage.setItem(getReadStorageKey(userKey), JSON.stringify(nextState));
            } catch {
                // Ignore storage errors.
            }
        },
        [userKey]
    );

    const teacherNameMap = useMemo(() => {
        const map = new Map<string, string>();
        (Array.isArray(giaoViens) ? giaoViens : []).forEach((item: any) => {
            if (item?.ID_cb) {
                map.set(String(item.ID_cb), item?.Ho_ten || String(item.ID_cb));
            }
        });
        return map;
    }, [giaoViens]);

    const lopNameMap = useMemo(() => {
        const map = new Map<number, string>();
        (Array.isArray(danhSachLop) ? danhSachLop : []).forEach((item: any) => {
            if (!item?.ID_lop_bskt) return;
            const label = [item?.Ky_hieu_lop, item?.Ten_lop_bskt].filter(Boolean).join(' - ');
            map.set(Number(item.ID_lop_bskt), label || String(item.ID_lop_bskt));
        });
        return map;
    }, [danhSachLop]);

    const resolveTeacherName = useCallback(
        (teacherId?: string | null) => {
            if (!teacherId) return '-';
            return teacherNameMap.get(String(teacherId)) || teacherId;
        },
        [teacherNameMap]
    );

    const buildManageReadKey = useCallback((item: UnlockRequestItem) => {
        return `manage:${item.id}:${item.status}:${item.requested_at || ''}`;
    }, []);

    const buildMineReadKey = useCallback((item: UnlockRequestItem) => {
        return `mine:${item.id}:${item.status}:${item.approved_at || item.rejected_at || item.requested_at || ''}`;
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const [pendingResult, mineResult] = await Promise.all([
                canManageRequests ? DiemService.getUnlockRequests('pending', false) : Promise.resolve([]),
                DiemService.getUnlockRequests(undefined, true)
            ]);

            const pendingItems = Array.isArray(pendingResult) ? pendingResult : [];
            const mineItems = new Map<number, UnlockRequestItem>();

            (Array.isArray(mineResult) ? mineResult : []).forEach((item) => {
                mineItems.set(item.id, mergeUnlockItem(mineItems.get(item.id), item));
            });

            pendingItems.forEach((item) => {
                const existing = mineItems.get(item.id);
                if (!existing) return;
                mineItems.set(item.id, mergeUnlockItem(existing, item));
            });

            setPending(pendingItems.sort((a, b) => Number(b.id || 0) - Number(a.id || 0)));
            setMine(Array.from(mineItems.values()).sort((a, b) => Number(b.id || 0) - Number(a.id || 0)));
        } catch (error) {
            console.error(error);
            setPending([]); setMine([]);
            setLoadError('Không tải được yêu cầu mở khóa. Vui lòng thử lại hoặc kiểm tra cấu hình máy chủ.');
        } finally {
            setLoading(false);
        }
    }, [canManageRequests, userKey]);

    useEffect(() => {
        void loadData();
        const refresh=()=>{void loadData();};
        window.addEventListener('unlock-req-changed',refresh);
        return()=>window.removeEventListener('unlock-req-changed',refresh);
    }, [loadData]);

    const markItemsRead = useCallback(
        (keys: string[]) => {
            if (!keys.length) return;
            const next = { ...readState };
            let changed = false;

            keys.forEach((key) => {
                if (!next[key]) {
                    next[key] = true;
                    changed = true;
                }
            });

            if (changed) {
                persistReadState(next);
            }
        },
        [persistReadState, readState]
    );

    const manageRows = useMemo(() => pending, [pending]);
    const mineRows = useMemo(() => mine, [mine]);
    const safeActiveTab: BellTabKey = canManageRequests ? activeTab : 'mine';
    const tabRows = safeActiveTab === 'manage' ? manageRows : mineRows;

    const manageUnreadItems = useMemo(() => {
        return manageRows.filter((item) => !readState[buildManageReadKey(item)]);
    }, [buildManageReadKey, manageRows, readState]);

    const mineUnreadItems = useMemo(() => {
        return mineRows.filter((item) => !readState[buildMineReadKey(item)]);
    }, [buildMineReadKey, mineRows, readState]);

    const unreadCount = (canManageRequests ? manageUnreadItems.length : 0) + mineUnreadItems.length;

    const detailStudents = useMemo(() => {
        return (detailItem?.student_details || '')
            .split(/\r?\n/)
            .map((item) => item.trim())
            .filter(Boolean);
    }, [detailItem]);

    const handleOpen = async () => {
        setActiveTab(canManageRequests && manageRows.length > 0 ? 'manage' : 'mine');
        setOpen(true);
        await loadData();
    };

    const handleApprove = async (item: UnlockRequestItem) => {
        if(processing !== null || !canManageRequests) return;
        setProcessing(item.id);
        try {
            const ok = await DiemService.approveUnlockRequest(item.id);
            if (!ok) {
                NotifyHelper.Error('Duyệt yêu cầu không thành công.');
                return;
            }
            NotifyHelper.Success('Đã duyệt yêu cầu mở khóa.');
            window.dispatchEvent(new Event('unlock-req-changed'));
            await loadData();
        } catch (error) {
            console.error(error);
            NotifyHelper.Error('Duyệt yêu cầu không thành công.');
        } finally { setProcessing(null); }
    };

    const handleReject = async () => {
        if (!rejecting || processing !== null || !canManageRequests) return;

        const reason = rejectReason.trim();
        if (!reason) {
            NotifyHelper.Warning('Vui lòng nhập lý do từ chối.');
            return;
        }

        try {
            setProcessing(rejecting.id);
            const ok = await DiemService.rejectUnlockRequest(rejecting.id, reason);
            if (!ok) {
                NotifyHelper.Error('Từ chối yêu cầu không thành công.');
                return;
            }
            NotifyHelper.Success('Đã từ chối yêu cầu mở khóa.');
            window.dispatchEvent(new Event('unlock-req-changed'));
            setRejecting(null);
            setRejectReason('');
            await loadData();
        } catch (error) {
            console.error(error);
            NotifyHelper.Error('Từ chối yêu cầu không thành công.');
        } finally { setProcessing(null); }
    };

    const tooltipTitle =
        unreadCount > 0
            ? `Có ${canManageRequests ? manageUnreadItems.length : 0} yêu cầu cần xử lý và ${mineUnreadItems.length} thông báo chưa đọc`
            : 'Không có thông báo mới';

    return (
        <>
            <Tooltip title={tooltipTitle}>
                <IconButton color="inherit" onClick={handleOpen} aria-label="Thông báo yêu cầu mở khóa">
                    <Badge badgeContent={unreadCount} color="error">
                        <NotificationsIcon sx={{ color: 'inherit' }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Dialog open={open} onClose={() => {if(processing===null)setOpen(false);}} maxWidth="xl" fullWidth>
                <DialogTitle>Trung tâm đề xuất mở khóa điểm</DialogTitle>
                <DialogContent dividers>
                  <div className="unlock-center">
                    <div className="unlock-center-intro"><div><span className="unlock-center-symbol"><i className="bi bi-bell"/></span><div><h3>Thông báo & yêu cầu mở khóa</h3><p>Theo dõi đề xuất, xem học viên và phản hồi của người duyệt.</p></div></div>
                      <Button variant="outlined" size="small" onClick={loadData} disabled={loading || processing !== null} startIcon={<i className="bi bi-arrow-clockwise"/>}>{loading?'Đang tải...':'Làm mới'}</Button>
                    </div>
                    {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}

                    <Tabs value={safeActiveTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 2 }}>
                        {canManageRequests && (
                            <Tab
                                value="manage"
                                label={`Cần xử lý (${manageRows.length}${manageUnreadItems.length > 0 ? ` • ${manageUnreadItems.length} chưa đọc` : ''})`}
                            />
                        )}
                        <Tab
                            value="mine"
                            label={`Thông báo của tôi (${mineRows.length}${mineUnreadItems.length > 0 ? ` • ${mineUnreadItems.length} chưa đọc` : ''})`}
                        />
                    </Tabs>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        {safeActiveTab === 'manage' ? (
                            <>
                                <Chip label={`Đang chờ duyệt: ${manageRows.length}`} color="warning" variant="outlined" />
                                <Chip label={`Chưa đọc: ${manageUnreadItems.length}`} color="info" variant="outlined" />
                            </>
                        ) : (
                            <>
                                <Chip label={`Tổng yêu cầu: ${mineRows.length}`} color="default" variant="outlined" />
                                <Chip label={`Đang chờ: ${mineRows.filter((item) => item.status === 'pending').length}`} color="warning" variant="outlined" />
                                <Chip label={`Đã phản hồi: ${mineRows.filter((item) => item.status !== 'pending').length}`} color="success" variant="outlined" />
                                <Chip label={`Chưa đọc: ${mineUnreadItems.length}`} color="info" variant="outlined" />
                            </>
                        )}
                    </Stack>

                    <div className="unlock-center-filters">
                      <div className="unlock-center-search"><i className="bi bi-search"/><input aria-label="Tìm yêu cầu mở khóa" placeholder="Tìm lớp, môn học hoặc lý do..." value={keyword} onChange={e=>setKeyword(e.target.value)}/></div>
                      {safeActiveTab==='mine' && <div className="unlock-status-filter"><Combobox
                        label="Lọc trạng thái yêu cầu" value={statusFilter} onChange={setStatusFilter}
                        options={[{value:'all',label:'Tất cả trạng thái'},{value:'pending',label:'Chờ duyệt'},{value:'approved',label:'Đã duyệt'},{value:'rejected',label:'Từ chối'}]}
                      /></div>}
                      <Button variant="text" onClick={()=>markItemsRead(tabRows.map(safeActiveTab==='manage'?buildManageReadKey:buildMineReadKey))} disabled={loading || !tabRows.length}>Đánh dấu đã đọc</Button>
                    </div>
                    <BootstrapTable loading={loading} emptyText={loadError ? 'Chưa tải được thông báo.' : keyword || statusFilter!=='all' ? 'Không tìm thấy yêu cầu phù hợp.' : safeActiveTab==='manage' ? 'Không có yêu cầu nào cần xử lý.' : 'Bạn chưa gửi đề xuất mở khóa nào.'}
                      data={tabRows.filter(item => (safeActiveTab==='manage'||statusFilter==='all'||item.status===statusFilter) && [formatLopText(item,lopNameMap.get(item.id_lop_bskt)),item.ten_mon,item.requested_reason].some(value=>(value||'').toLocaleLowerCase('vi').includes(keyword.trim().toLocaleLowerCase('vi'))))}
                      columns={[
                        {key:'class',header:'Lớp / môn học',minWidth:210,render:(item:UnlockRequestItem)=><div className="unlock-class"><strong>{formatLopText(item,lopNameMap.get(item.id_lop_bskt))}</strong><span>{item.ten_mon || 'Chưa có môn học'}</span></div>},
                        {key:'status',header:'Trạng thái',minWidth:125,render:(item:UnlockRequestItem)=>statusChip(item.status)},
                        {key:'request',header:'Đề xuất',minWidth:220,render:(item:UnlockRequestItem)=><div className="unlock-class"><strong>{resolveTeacherName(item.requested_by)}</strong><span>{item.requested_reason || 'Không có lý do'}</span><small>{item.requested_at?<Moment format="DD/MM/YYYY HH:mm">{item.requested_at}</Moment>:'—'}</small></div>},
                        {key:'students',header:'Học viên',width:100,render:(item:UnlockRequestItem)=><Button variant="text" onClick={()=>{setDetailItem(item);markItemsRead([(safeActiveTab==='manage'?buildManageReadKey:buildMineReadKey)(item)]);}}>{item.so_thi_sinh ?? 0} <i className="bi bi-chevron-right"/></Button>},
                        {key:'result',header:safeActiveTab==='manage'?'Xử lý':'Phản hồi',minWidth:210,render:(item:UnlockRequestItem)=>safeActiveTab==='manage'?<div className="unlock-row-actions"><Button disabled={processing !== null || loading} onClick={()=>handleApprove(item)} startIcon={<DoneIcon/>}>{processing===item.id?'Đang xử lý...':'Duyệt'}</Button><Button disabled={processing !== null || loading} variant="outlined" color="error" onClick={()=>{setRejecting(item);setRejectReason('');}}>Từ chối</Button></div>:<div className="unlock-class"><span>{item.status==='pending'?'Đang chờ người có quyền duyệt xử lý.':resolveTeacherName(item.status==='approved'?item.approved_by:item.rejected_by)}</span>{item.status==='rejected'&&<span className="text-danger">{item.rejected_reason || 'Không có lý do từ chối'}</span>}{item.status!=='pending'&&<small>{item.approved_at || item.rejected_at ? <Moment format="DD/MM/YYYY HH:mm">{item.approved_at || item.rejected_at || undefined}</Moment> : '—'}</small>}</div>}
                      ]}/>
                  </div>
                </DialogContent>

                <DialogActions>
                    <Button disabled={processing !== null} onClick={() => setOpen(false)} variant="contained">
                        Đóng
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!rejecting}
                onClose={() => {
                    setRejecting(null);
                    setRejectReason('');
                }}
                maxWidth="sm"
                fullWidth>
                <DialogTitle>Từ chối yêu cầu mở khóa</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Lý do từ chối"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setRejecting(null);
                        setRejectReason('');
                    }}>Hủy</Button>
                    <Button disabled={processing !== null || !rejectReason.trim()} variant="contained" color="error" onClick={handleReject}>
                        Xác nhận từ chối
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!detailItem} onClose={() => setDetailItem(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Danh sách sinh viên được đề xuất mở khóa</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                            Lớp: {detailItem ? formatLopText(detailItem, lopNameMap.get(Number(detailItem.id_lop_bskt || 0))) : '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Môn: {detailItem?.ten_mon || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Số sinh viên: {detailItem?.so_thi_sinh ?? 0}
                        </Typography>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                        {detailStudents.length === 0 ? (
                            <Typography color="text.secondary">Chưa có thông tin sinh viên.</Typography>
                        ) : (
                            <Stack spacing={1}>
                                {detailStudents.map((student, index) => (
                                    <Box
                                        key={`${student}-${index}`}
                                        sx={{
                                            px: 1.5,
                                            py: 1,
                                            border: '1px solid #d9e2f2',
                                            borderRadius: 2,
                                            backgroundColor: '#f8fbff'
                                        }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {index + 1}. {student}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setDetailItem(null)}>
                        Đóng
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UnlockRequestBell;
